import { ReactNode, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import UserSidebar from './UserSidebar';
import { PageTitleProvider } from '../../hooks/usePageTitle';
import { HospitalOhpDomainProvider } from '../../hooks/useHospitalOhpDomains';
import { UserSidebarProvider, useUserSidebar } from '../../hooks/useUserSidebar';
import { SavedItemsProvider } from '../../hooks/useSavedItems';
import { AuthModalProvider } from '../auth/AuthModalContext';
import AuthModal from '../auth/AuthModal';
import { wantsProgramScanRestore } from '../../utils/programScan';

interface LayoutProps {
  children: ReactNode;
}

const scrollByHistoryKey = new Map<string, number>();
const scrollByPath = new Map<string, number>();

export function savedHistoryScroll(key: string): number | undefined {
  return scrollByHistoryKey.get(key);
}

function pathScrollId(pathname: string, search: string, hash: string) {
  return `${pathname}${search}${hash}`;
}

function wantsScrollRestore(state: unknown) {
  return Boolean(
    state && typeof state === 'object' && (state as { restoreScroll?: boolean }).restoreScroll
  );
}

function restoreScroll(y: number) {
  if (y <= 0) {
    window.scrollTo(0, 0);
    return;
  }

  const started = performance.now();
  let lastHeight = -1;
  let stable = 0;
  let timer = 0;
  let stopped = false;

  const tick = () => {
    if (stopped) return;
    window.scrollTo(0, y);
    const height = document.documentElement.scrollHeight;
    stable = height === lastHeight ? stable + 1 : 0;
    lastHeight = height;
    const max = Math.max(0, height - window.innerHeight);
    const placed = max + 2 >= y && Math.abs(window.scrollY - y) < 2;
    if ((placed && stable >= 3) || performance.now() - started > 5000) return;
    timer = window.setTimeout(tick, 50);
  };

  tick();
  return () => {
    stopped = true;
    window.clearTimeout(timer);
  };
}

function ScrollRestoration() {
  const { pathname, search, hash, key, state } = useLocation();
  const navigationType = useNavigationType();
  const pathnameRef = useRef(pathname);

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let frame = 0;
    const save = () => {
      const y = window.scrollY;
      scrollByHistoryKey.set(key, y);
      scrollByPath.set(pathScrollId(pathname, search, hash), y);
    };
    const onScroll = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(save);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      save();
      window.removeEventListener('scroll', onScroll);
    };
  }, [key, pathname, search, hash]);

  useLayoutEffect(() => {
    const previousPath = pathnameRef.current;
    pathnameRef.current = pathname;

    if (navigationType === 'POP') {
      const saved = scrollByHistoryKey.get(key);
      if (saved != null) return restoreScroll(saved);
      return;
    }

    if (wantsProgramScanRestore(state)) return;

    if (wantsScrollRestore(state)) {
      return restoreScroll(scrollByPath.get(pathScrollId(pathname, search, hash)) ?? 0);
    }

    const hubPlace = hash.startsWith('#hub-') && !hash.startsWith('#hub-section-');
    if (previousPath !== pathname && (!hash || hubPlace)) {
      window.scrollTo(0, 0);
    }
  }, [pathname, search, hash, key, state, navigationType]);

  return null;
}

function LayoutShell({ children }: LayoutProps) {
  const { isDocked, panelPaddingPx } = useUserSidebar();

  return (
    <>
      <ScrollRestoration />
      <div className="min-h-screen flex flex-col bg-white">
        <div
          className={`
            layout-page
            flex-1 flex flex-col
            ${isDocked ? 'my-orbcon-open' : ''}
            transition-[padding] duration-150 ease-in-out motion-reduce:transition-none
          `}
          style={{ paddingRight: panelPaddingPx }}
        >
          <Header />
          <main className="flex-1 bg-white">
            {children}
          </main>
          <Footer />
        </div>
      </div>
      <UserSidebar />
      <AuthModal />
    </>
  );
}

export default function Layout({ children }: LayoutProps) {
  return (
    <PageTitleProvider>
      <HospitalOhpDomainProvider>
        <AuthModalProvider>
          <SavedItemsProvider>
            <UserSidebarProvider>
              <LayoutShell>{children}</LayoutShell>
            </UserSidebarProvider>
          </SavedItemsProvider>
        </AuthModalProvider>
      </HospitalOhpDomainProvider>
    </PageTitleProvider>
  );
}
